import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { getAdminToken } from "../lib/api";
import { cartCount, subscribeCart } from "../lib/cart";
import { useCustomer } from "../contexts/CustomerContext";

const RawHeader = ({ variant = "public" }) => {
  const isAdmin = !!getAdminToken();
  const loc = useLocation();
  const currentGender = (new URLSearchParams(loc.search).get("gender") || "all").toLowerCase();
  const [basketCount, setBasketCount] = useState(cartCount());
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => subscribeCart(() => setBasketCount(cartCount())), []);
  // Close mobile menu when the route changes
  useEffect(() => { setMenuOpen(false); }, [loc.pathname, loc.search]);
  const { customer } = useCustomer();

  const publicLinks = [
    { to: "/", label: "Shop", active: loc.pathname === "/" && currentGender === "all", testid: "nav-shop" },
    { to: "/?gender=men", label: "Men", active: currentGender === "men", testid: "nav-men" },
    { to: "/?gender=women", label: "Women", active: currentGender === "women", testid: "nav-women" },
  ];

  const adminLinks = [
    { to: "/admin", label: "Designs", active: loc.pathname === "/admin", testid: "nav-admin-dash" },
    { to: "/admin/logo", label: "Logo Studio", active: loc.pathname.startsWith("/admin/logo"), testid: "nav-admin-logo" },
    { to: "/admin/orders", label: "Orders", active: loc.pathname.startsWith("/admin/orders"), testid: "nav-admin-orders" },
    { to: "/admin/finance", label: "Finance", active: loc.pathname.startsWith("/admin/finance"), testid: "nav-admin-finance" },
  ];

  return (
    <>
      {/* Engraved brass plate marker strip */}
      <div
        data-testid="top-strip"
        className="brass-plate w-full text-[10px] font-mono uppercase tracking-[0.3em] flex items-center justify-between px-4 sm:px-6 py-2"
      >
        <span className="brass-rivet brass-rivet--left" aria-hidden="true" />
        <span className="engraved-text">// SYSTEM ONLINE</span>
        <span className="engraved-text hidden sm:inline">FACTORY-LINKED / TECH-PACK ENGINE / V1.0</span>
        <span className="engraved-text">{new Date().toISOString().slice(0, 10)}</span>
        <span className="brass-rivet brass-rivet--right" aria-hidden="true" />
      </div>

      <header className="sticky top-0 z-40 bg-[#050505]/95 backdrop-blur border-b border-zinc-800">
        <div className="relative flex items-center justify-between px-4 sm:px-6 lg:px-10 py-4 sm:py-5 gap-3">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group min-w-0">
            <div
              className="w-20 h-20 sm:w-32 sm:h-32 -mb-8 sm:-mb-14 bg-black border-2 border-[#d4af37] flex items-center justify-center overflow-hidden shadow-[0_6px_20px_rgba(0,0,0,0.7),0_0_0_1px_rgba(212,175,55,0.35)] relative z-50 flex-shrink-0"
            >
              <img src="/raw-company-logo.jpeg?v=4" alt="Raw Company" className="w-full h-full object-contain" />
            </div>
            <div className="leading-tight min-w-0">
              <div className="font-display text-lg sm:text-2xl tracking-tighter text-[#d4af37] truncate">RAW COMPANY</div>
              <div className="text-[8px] sm:text-[9px] tracking-[0.32em] sm:tracking-[0.4em] text-[#d4af37]/70 uppercase truncate">
                Premium UK Streetwear
              </div>
            </div>
          </Link>

          {/* ---------------- DESKTOP NAV (sm and up) ---------------- */}
          <nav className="hidden sm:flex items-center gap-1">
            {variant === "public" ? (
              <>
                {publicLinks.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={l.testid}
                    className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] font-bold transition-colors ${
                      l.active ? "text-[#d4af37]" : "text-zinc-400 hover:text-white"
                    }`}
                  >
                    {l.label}
                  </Link>
                ))}
                <Link
                  to="/cart"
                  data-testid="nav-cart"
                  className="relative px-3 py-2 text-[11px] uppercase tracking-[0.2em] font-bold text-zinc-400 hover:text-[#d4af37] transition-colors"
                  aria-label={`Basket with ${basketCount} items`}
                >
                  Basket
                  {basketCount > 0 && (
                    <span
                      data-testid="cart-count-badge"
                      className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[9px] font-bold bg-[#d4af37] text-black rounded-full grid place-items-center"
                    >
                      {basketCount}
                    </span>
                  )}
                </Link>
                {customer ? (
                  <Link to="/account" data-testid="nav-account" className="px-3 py-2 text-[11px] uppercase tracking-[0.2em] font-bold text-zinc-400 hover:text-[#d4af37] transition-colors">Account</Link>
                ) : (
                  <Link to="/login" data-testid="nav-signin" className="px-3 py-2 text-[11px] uppercase tracking-[0.2em] font-bold text-zinc-400 hover:text-[#d4af37] transition-colors">Sign in</Link>
                )}
                <Link to="/admin/login" data-testid="nav-admin" className="ml-2 px-5 py-2 text-[11px] uppercase tracking-[0.2em] font-bold border border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-colors">
                  {isAdmin ? "Admin" : "Login"}
                </Link>
              </>
            ) : (
              <>
                {adminLinks.map((l) => (
                  <Link key={l.to} to={l.to} data-testid={l.testid} className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] font-bold transition-colors ${l.active ? "text-[#d4af37]" : "text-zinc-400 hover:text-white"}`}>
                    {l.label}
                  </Link>
                ))}
                <Link to="/" data-testid="nav-view-shop" className="ml-3 px-5 py-2 text-[11px] uppercase tracking-[0.2em] font-bold border border-white hover:bg-white hover:text-black transition-colors">View Shop</Link>
              </>
            )}
          </nav>

          {/* ---------------- MOBILE CONTROLS ---------------- */}
          <div className="flex sm:hidden items-center gap-2">
            {variant === "public" && (
              <Link
                to="/cart"
                data-testid="nav-cart-mobile"
                className="relative w-10 h-10 grid place-items-center border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-colors"
                aria-label={`Basket with ${basketCount} items`}
              >
                {/* Simple bag icon — no external font deps */}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" aria-hidden="true">
                  <path d="M6 7h12l-1 13H7L6 7z" />
                  <path d="M9 7a3 3 0 0 1 6 0" />
                </svg>
                {basketCount > 0 && (
                  <span
                    data-testid="cart-count-badge-mobile"
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[9px] font-bold bg-[#d4af37] text-black rounded-full grid place-items-center"
                  >
                    {basketCount}
                  </span>
                )}
              </Link>
            )}
            <button
              data-testid="menu-toggle"
              onClick={() => setMenuOpen((v) => !v)}
              className="w-10 h-10 grid place-items-center border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-colors"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
            >
              {menuOpen ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="18" y1="6" x2="6" y2="18" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <line x1="4" y1="7" x2="20" y2="7" />
                  <line x1="4" y1="12" x2="20" y2="12" />
                  <line x1="4" y1="17" x2="20" y2="17" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* ---------------- MOBILE DROPDOWN MENU ---------------- */}
        {menuOpen && (
          <div data-testid="mobile-menu" className="sm:hidden border-t border-[#d4af37]/30 bg-[#050505]/98 backdrop-blur-md">
            <div className="flex flex-col px-4 py-3 gap-1">
              {variant === "public" ? (
                <>
                  {publicLinks.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      data-testid={`${l.testid}-mobile`}
                      className={`px-3 py-3 text-sm uppercase tracking-[0.2em] font-bold border-b border-zinc-900 ${
                        l.active ? "text-[#d4af37]" : "text-zinc-300"
                      }`}
                    >
                      {l.label}
                    </Link>
                  ))}
                  {customer ? (
                    <Link to="/account" data-testid="nav-account-mobile" className="px-3 py-3 text-sm uppercase tracking-[0.2em] font-bold text-zinc-300 border-b border-zinc-900">Account</Link>
                  ) : (
                    <Link to="/login" data-testid="nav-signin-mobile" className="px-3 py-3 text-sm uppercase tracking-[0.2em] font-bold text-zinc-300 border-b border-zinc-900">Sign in</Link>
                  )}
                  <Link
                    to="/admin/login"
                    data-testid="nav-admin-mobile"
                    className="mt-2 px-4 py-3 text-sm uppercase tracking-[0.2em] font-bold border border-[#d4af37] text-[#d4af37] text-center"
                  >
                    {isAdmin ? "Admin Panel" : "Admin Login"}
                  </Link>
                </>
              ) : (
                <>
                  {adminLinks.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      data-testid={`${l.testid}-mobile`}
                      className={`px-3 py-3 text-sm uppercase tracking-[0.2em] font-bold border-b border-zinc-900 ${
                        l.active ? "text-[#d4af37]" : "text-zinc-300"
                      }`}
                    >
                      {l.label}
                    </Link>
                  ))}
                  <Link
                    to="/"
                    data-testid="nav-view-shop-mobile"
                    className="mt-2 px-4 py-3 text-sm uppercase tracking-[0.2em] font-bold border border-white text-white text-center"
                  >
                    View Shop
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default RawHeader;
